import React, { useEffect, useState } from "react";
import { 
  IndianRupee, 
  FileText, 
  Users, 
  UserX, 
  Calendar,
  Camera,
  Clock,
  MapPin
} from "lucide-react";

export default function Dashboard() {

  /* ================= STATE ================= */
  const [attendanceData, setAttendanceData] = useState([]);
  const [present, setPresent] = useState(0);
  const [absent, setAbsent] = useState(0);
  const [totalStaff, setTotalStaff] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  /* ================= FETCH TODAY ATTENDANCE ================= */
  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const res = await fetch("https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/attendance/dashboard-today");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        setAttendanceData(Array.isArray(data.attendance) ? data.attendance : []);
        setPresent(Number(data.present) || 0);
        setAbsent(Number(data.absent) || 0);
        setTotalStaff(Number(data.totalStaff) || 0);
      } catch (err) {
        console.error("❌ Dashboard fetch failed:", err);
        setAttendanceData([]);
      }
    };

    loadDashboard();
  }, []);

  /* ================= MAP HANDLER (FIXED) ================= */
  const handleViewLocation = (locationString) => {
    if (!locationString) {
      alert("No location data available.");
      return;
    }

    // Input format is usually: "Lat: 12.345678, Long: 98.765432"
    // We need to extract just the numbers: "12.345678,98.765432"
    
    // Regex to find the numbers (including negative signs for S/W coordinates)
    const matches = locationString.match(/Lat:\s*([0-9.-]+),\s*Long:\s*([0-9.-]+)/);

    let mapUrl;
    
    if (matches && matches[1] && matches[2]) {
      const lat = matches[1];
      const long = matches[2];
      // Construct a clean Google Maps query URL
      mapUrl = `https://www.google.com/maps?q=${lat},${long}`;
    } else {
      // Fallback: If format is different, try searching the raw string
      console.warn("Location format mismatch, using raw query");
      mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(locationString)}`;
    }
    
    window.open(mapUrl, "_blank");
  };

  return (
    <div className="p-6 md:p-10 space-y-6 bg-slate-950 min-h-screen text-slate-200 font-sans">

      {/* --- Header --- */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 text-sm">Overview of daily staff attendance</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-900/80 px-4 py-2 rounded-full border border-slate-800 shadow-sm">
          <Clock size={14} className="text-indigo-400" />
          {new Date().toLocaleDateString("en-IN", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </div>
      </div>

      {/* --- Stats Row --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatCard title="Daily Profit" value="₹ 12,450" icon={IndianRupee} color="from-emerald-500 to-teal-500" />
        <StatCard title="Monthly Profit" value="₹ 4.2 Lac" icon={Calendar} color="from-indigo-500 to-blue-500" />
        <StatCard title="Bills Generated" value="142" icon={FileText} color="from-orange-500 to-amber-500" />
        <StatCard
          title="Staff Present"
          value={`${present} / ${totalStaff}`}
          subLabel={`${totalStaff ? Math.round((present / totalStaff) * 100) : 0}% Attendance`}
          icon={Users}
          color="from-sky-500 to-cyan-500"
        />
        <StatCard title="On Leave" value={absent} icon={UserX} color="from-rose-500 to-pink-500" isAlert />
      </div>

      {/* --- Attendance Table --- */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">

        <div className="p-5 border-b border-slate-800 flex justify-between items-center bg-slate-900/80">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Live Attendance
            </h2>
            <p className="text-xs text-slate-500 mt-1">Real-time GPS & Photo verification</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-xs font-semibold text-slate-400 border-b border-slate-800 bg-slate-900/40 uppercase tracking-wider">
                <th className="p-4 text-left">Employee</th>
                <th className="p-4 text-left">Time In</th>
                <th className="p-4 text-right">Verification</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {attendanceData.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-12 text-center flex flex-col items-center justify-center text-slate-500">
                    <Clock className="w-10 h-10 mb-2 opacity-20" />
                    <span className="text-sm">No attendance records found for today.</span>
                  </td>
                </tr>
              ) : (
                attendanceData.map((emp, i) => (
                  <tr key={i} className="hover:bg-slate-800/40 transition-colors group">
                    <td className="p-4">
                      <div className="flex gap-3 items-center">
                        <div className="relative">
                          <img
                            src={`https://ui-avatars.com/api/?name=${emp.name}&background=random&color=fff`}
                            alt={emp.name}
                            className="w-10 h-10 rounded-full border-2 border-slate-700 shadow-sm"
                          />
                          <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-slate-900 rounded-full"></span>
                        </div>
                        <div>
                          <div className="text-slate-200 font-medium">{emp.name}</div>
                          <div className="text-xs text-slate-500">{emp.email}</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-slate-300 bg-slate-800/50 w-fit px-3 py-1 rounded-lg border border-slate-700/50">
                        <Clock size={14} className="text-blue-400" /> 
                        <span className="font-mono text-sm">{emp.time || "--:--"}</span>
                      </div>
                    </td>

                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        
                        {/* VIEW LOCATION BUTTON */}
                        <button
                          onClick={() => handleViewLocation(emp.location)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-emerald-900/30 text-xs font-medium text-emerald-400 border border-slate-700 hover:border-emerald-700 rounded-lg transition-all"
                          title={emp.location || "No location data"}
                        >
                          <MapPin size={14} />
                          <span className="hidden sm:inline">Map</span>
                        </button>

                        {/* VIEW PHOTO BUTTON */}
                        <button
                          onClick={() =>
                            setSelectedPhoto(
                              `https://api-dg3-core-inv5.adminpaymentdailygo.co.in/uploads/attendance/${emp.photo}`
                            )
                          }
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-xs font-medium text-white border border-transparent rounded-lg shadow-lg shadow-indigo-900/20 transition-all"
                        >
                          <Camera size={14} />
                          <span className="hidden sm:inline">Photo</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 text-xs text-slate-500 text-center border-t border-slate-800 bg-slate-900/30">
          Showing {attendanceData.length} records • Data refreshes on reload
        </div>
      </div>

      {/* ================= PHOTO MODAL ================= */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedPhoto(null)}
        >
          <div
            className="bg-slate-900 p-2 rounded-2xl max-w-sm w-full shadow-2xl border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative aspect-[3/4] w-full bg-slate-950 rounded-xl overflow-hidden">
               <img 
                 src={selectedPhoto} 
                 alt="Verification" 
                 className="w-full h-full object-cover" 
                 onError={(e) => { e.target.src = "https://via.placeholder.com/400x600?text=Image+Error"; }}
               />
               <div className="absolute top-3 right-3 bg-black/50 backdrop-blur px-2 py-1 rounded text-xs text-white flex items-center gap-1">
                 <Camera size={12} /> Verified
               </div>
            </div>
            <button
              onClick={() => setSelectedPhoto(null)}
              className="mt-3 w-full py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-colors"
            >
              Close Viewer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= STAT CARD COMPONENT ================= */
function StatCard({ title, value, subLabel, icon: Icon, color, isAlert }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-2xl hover:bg-slate-800/60 transition-colors group">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">{title}</p>
          <h3 className={`text-2xl font-bold mt-1 ${isAlert ? "text-rose-400" : "text-white"}`}>{value}</h3>
          {subLabel && <p className="text-xs text-slate-500 mt-1">{subLabel}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${color} text-white shadow-lg opacity-80 group-hover:opacity-100 transition-opacity`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}